import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useMemo, useState } from "react";

import Close from "@mui/icons-material/Close";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FullscreenExitOutlined from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlined from "@mui/icons-material/FullscreenOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { grey } from "@mui/material/colors";
import { useBorrowerPortalHook } from "../../hooks/useBorrowerPortalHook";
import FilePreviewImageTypeIcon from "../common/FilePreviewImageTypeIcon";

const BorrowerFilePreviewModal = ({
  fileObject,
  borrowerId,
  onClose,
  open,
}) => {
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { loading, getBorrowerProfileDocumentById } = useBorrowerPortalHook();

  const content_type = fileObject?.content_type;
  const file_display_name = fileObject?.file_display_name;
  const file_storage_name = fileObject?.file_storage_name;
  const isImage = content_type?.startsWith("image/");
  const isPdf = content_type === "application/pdf";

  // ── Fetch file blob ──────────────────────────────────────────
  const fetchFile = useCallback(() => {
    if (!borrowerId || !file_storage_name) return;
    setError(null);

    getBorrowerProfileDocumentById({
      borrowerId,
      file_storage_name,
      isFile: true,
      onSuccessFn: (responseData) => {
        try {
          const blob =
            responseData instanceof Blob
              ? responseData
              : new Blob([responseData], { type: content_type });
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        } catch (err) {
          console.error("Error creating Blob URL:", err);
          setError(
            "Failed to process the file. The format may be unsupported.",
          );
        }
      },
      onFailFn: (err) => {
        console.error("Error fetching borrower file:", err);
        setError("Failed to load the file. Please try again.");
      },
    });
  }, [
    borrowerId,
    file_storage_name,
    content_type,
    getBorrowerProfileDocumentById,
  ]);

  useEffect(() => {
    if (open && fileObject && file_storage_name && borrowerId) {
      setActiveTab(0);
      fetchFile();
    }
  }, [open, fileObject]);

  // Cleanup blob URL on unmount / URL change
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleClose = useCallback(() => {
    setPreviewUrl(null);
    setError(null);
    setActiveTab(0);
    setIsFullscreen(false);
    if (isFunction(onClose)) onClose();
  }, [onClose]);

  const handleDownload = useCallback(() => {
    if (!borrowerId || !file_storage_name) return;
    getBorrowerProfileDocumentById({
      borrowerId,
      file_storage_name,
      isFile: true,
      fileDownload: true,
      fileDownloadName: file_display_name || file_storage_name,
    });
  }, [
    borrowerId,
    file_storage_name,
    file_display_name,
    getBorrowerProfileDocumentById,
  ]);

  const handleOpenInNewTab = useCallback(() => {
    if (!borrowerId || !file_storage_name) return;
    getBorrowerProfileDocumentById({
      borrowerId,
      file_storage_name,
      isFile: true,
      openInNewTab: true,
    });
  }, [borrowerId, file_storage_name, getBorrowerProfileDocumentById]);

  // ── Detail rows for the "Details" tab ────────────────────────
  const detailRows = useMemo(() => {
    if (isNil(fileObject)) return [];
    return [
      { label: "Document Group", value: fileObject?.docGroup },
      { label: "Document Type", value: fileObject?.docType },
      { label: "Status", value: fileObject?.status },
      { label: "Content Type", value: content_type },
      { label: "Upload Date", value: fileObject?.uploadDate },
      { label: "Modified Date", value: fileObject?.modifiedDate },
      { label: "Uploaded By", value: fileObject?.uploadedBy },
    ].filter((row) => !isNil(row.value));
  }, [fileObject, content_type]);

  const paperSx = useMemo(() => {
    if (isFullscreen) {
      return {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh",
        maxHeight: "100vh",
        borderRadius: 0,
        margin: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease-in-out",
      };
    }
    return {
      width: 720,
      maxWidth: "95vw",
      height: "85vh",
      maxHeight: 860,
      borderRadius: "12px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      transition: "all 0.2s ease-in-out",
    };
  }, [isFullscreen]);

  if (isNil(fileObject)) return null;

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth={false}
      fullScreen={isFullscreen}
      PaperProps={{ sx: paperSx }}
    >
      {/* ── Header ───────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          background: "#fff",
        }}
      >
        {/* Left: icon + file name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            minWidth: 0,
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              background: grey[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FilePreviewImageTypeIcon contentType={content_type} size={22} />
          </Box>
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Tooltip title={file_display_name || "File Preview"}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file_display_name || "File Preview"}
              </Typography>
            </Tooltip>
            {(fileObject?.docGroup || fileObject?.docType) && (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: grey[500],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {[fileObject?.docGroup, fileObject?.docType]
                  .filter(Boolean)
                  .join(" › ")}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: action buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            ml: 1,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Download">
            <IconButton size="small" onClick={handleDownload}>
              <DownloadOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <IconButton size="small" onClick={handleOpenInNewTab}>
              <OpenInNewOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            <IconButton size="small" onClick={() => setIsFullscreen((f) => !f)}>
              {isFullscreen ? (
                <FullscreenExitOutlined sx={{ fontSize: 20 }} />
              ) : (
                <FullscreenOutlined sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <IconButton size="small" onClick={handleClose}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", background: "#fff" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            px: 2,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontSize: "12.5px",
              fontWeight: 600,
              py: 0,
            },
            "& .MuiTabs-indicator": {
              height: 2,
              borderRadius: 1,
            },
          }}
        >
          <Tab label="Preview" />
          <Tab label="Details" />
        </Tabs>
      </Box>

      {/* ── Tab Content ──────────────────────────────────────── */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          position: "relative",
          bgcolor: grey[50],
        }}
      >
        {/* Preview Tab */}
        {activeTab === 0 && (
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            {/* Loading */}
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.85)",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={32} />
                <Typography
                  sx={{ mt: 1.5, fontSize: "13px", color: grey[600] }}
                >
                  Loading file…
                </Typography>
              </Box>
            )}

            {/* Error */}
            {!loading && error && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                <ErrorOutline color="error" sx={{ fontSize: 40 }} />
                <Typography
                  color="error"
                  sx={{
                    mt: 1.5,
                    fontSize: "13px",
                    textAlign: "center",
                    px: 3,
                  }}
                >
                  {error}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchFile}
                  sx={{ mt: 2 }}
                >
                  Try Again
                </Button>
              </Box>
            )}

            {/* Image preview */}
            {!loading && !error && previewUrl && isImage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  background: "#282828",
                  p: 2,
                }}
              >
                <img
                  src={previewUrl}
                  alt={file_display_name || "Image preview"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
              </Box>
            )}

            {/* PDF preview */}
            {!loading && !error && previewUrl && isPdf && (
              <iframe
                src={previewUrl}
                title={file_display_name || "PDF preview"}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  position: "absolute",
                  inset: 0,
                }}
              />
            )}

            {/* Unsupported type */}
            {!loading && !error && previewUrl && !isImage && !isPdf && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                }}
              >
                <FilePreviewImageTypeIcon
                  contentType={content_type}
                  size={48}
                />
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "13px",
                    color: grey[600],
                    textAlign: "center",
                  }}
                >
                  This file type ({content_type}) cannot be previewed.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadOutlined />}
                  onClick={handleDownload}
                  sx={{ mt: 2 }}
                >
                  Download File
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Details Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2, overflowY: "auto", height: "100%" }}>
            {detailRows.map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1.25,
                  borderBottom: `1px solid ${grey[200]}`,
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", color: grey[500], fontWeight: 500 }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{ fontSize: "13px", color: grey[800], fontWeight: 500 }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default BorrowerFilePreviewModal;
